/**
 * LightboxViewer — modal fullscreen para navegar fotos de progreso.
 *
 * Props:
 *   photos: [{ src, phase, percentage, date, description }]
 *   initialIndex: number
 *   onClose: fn
 */
import React, { useEffect, useState, useCallback } from "react";
import { X, CaretLeft, CaretRight, Camera, Trash } from "@phosphor-icons/react";
import useInvestorLanguage from "@/i18n/useInvestorLanguage";
import { acquireBodyScrollLock } from "@/lib/bodyScrollLock";

const MEDIA_FALLBACK = "Este formato puede no reproducirse en este navegador. Prueba en Safari/iPhone o sube un MP4.";

const PHASE_TKEY = {
  foundation: "progress.phase.foundation",
  structure:  "progress.phase.structure",
  finishes:   "progress.phase.finishes",
  delivery:   "progress.phase.delivery",
};

export default function LightboxViewer({ photos, initialIndex = 0, onClose, onRemove, canRemovePhoto }) {
  const { t, lang } = useInvestorLanguage();
  const dateLocale = lang === "en" ? "en-GB" : "es-ES";
  const [idx, setIdx] = useState(initialIndex);
  const [imgFailed, setImgFailed] = useState(false);
  const total = photos.length;

  const go = useCallback((delta) => {
    setImgFailed(false);
    setIdx((i) => (i + delta + total) % total);
  }, [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    // Freeze body scroll while open — ref-counted to survive nested overlays.
    const releaseScrollLock = acquireBodyScrollLock();
    return () => {
      window.removeEventListener("keydown", onKey);
      releaseScrollLock();
    };
  }, [go, onClose]);

  useEffect(() => { setImgFailed(false); }, [idx]);

  if (total === 0) return null;
  const p = photos[idx];
  const dateStr = p.date
    ? new Date(p.date).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "rgba(10,10,10,0.92)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="lightbox-backdrop"
    >
      {/* Close — high-contrast solid pill, sits above iOS safe-area so the
          notch/dynamic island doesn't clip the tap target. 44 px hit box
          meets Apple's HIG minimum for touch. */}
      <button
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label={t("lightbox.close")}
        className="absolute w-11 h-11 rounded-full bg-black/70 hover:bg-black/85 text-white flex items-center justify-center transition z-10 ring-2 ring-white/80 shadow-lg"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 12px)",
          right: "calc(env(safe-area-inset-right, 0px) + 12px)",
        }}
      >
        <X size={22} weight="bold" />
      </button>

      {/* Remove — only when handler provided AND current item has a file_id
          (legacy inline base64 items can't be deleted via /files/{id}). When
          `canRemovePhoto` is provided, it has the final say (e.g. constructor
          can only remove files they own). Positioned to the left of the close
          button, tracking the same safe-area offsets. */}
      {onRemove && p.fileId && (!canRemovePhoto || canRemovePhoto(p)) && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(p.fileId); }}
          data-testid="lightbox-remove"
          aria-label="Quitar archivo"
          title="Quitar archivo"
          className="absolute w-11 h-11 rounded-full bg-black/70 hover:bg-red-500/85 text-white flex items-center justify-center transition z-10 ring-2 ring-white/80 shadow-lg"
          style={{
            top: "calc(env(safe-area-inset-top, 0px) + 12px)",
            right: "calc(env(safe-area-inset-right, 0px) + 68px)",
          }}
        >
          <Trash size={20} weight="bold" />
        </button>
      )}

      {/* Image area */}
      <div className="flex-1 flex items-center justify-center relative px-4 sm:px-16 py-16"
        onClick={(e) => e.stopPropagation()}>
        {/* Prev */}
        {total > 1 && (
          <button
            onClick={() => go(-1)}
            data-testid="lightbox-prev"
            aria-label={t("lightbox.prev")}
            className="absolute left-3 sm:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <CaretLeft size={22} weight="bold" />
          </button>
        )}

        {imgFailed ? (
          <div className="flex flex-col items-center justify-center text-white/70 gap-3 px-6 text-center max-w-md" data-testid="lightbox-image-error">
            <Camera size={48} weight="light" />
            <div className="text-sm">
              {p.isVideo ? MEDIA_FALLBACK : t("lightbox.unavailable")}
            </div>
            {p.filename && (
              <div className="text-xs text-white/50 break-all">{p.filename}</div>
            )}
          </div>
        ) : p.isVideo ? (
          <video
            src={p.src}
            controls
            autoPlay
            playsInline
            preload="metadata"
            data-testid="lightbox-video"
            crossOrigin={p.useCredentials ? "use-credentials" : undefined}
            onError={() => setImgFailed(true)}
            style={{
              maxWidth: "100%",
              maxHeight: "calc(100dvh - 12rem)",
              objectFit: "contain",
              background: "#000",
            }}
            className="rounded-md shadow-xl"
          />
        ) : (
          <img
            src={p.src}
            alt=""
            data-testid="lightbox-image"
            crossOrigin={p.useCredentials ? "use-credentials" : undefined}
            onError={() => setImgFailed(true)}
            style={{
              maxWidth: "100%",
              // Use dvh so iOS Safari's dynamic chrome doesn't push the
              // image past the visible area as the URL bar collapses/expands.
              maxHeight: "calc(100dvh - 12rem)",
              objectFit: "contain",
            }}
            className="rounded-md shadow-xl"
          />
        )}

        {/* Next */}
        {total > 1 && (
          <button
            onClick={() => go(1)}
            data-testid="lightbox-next"
            aria-label={t("lightbox.next")}
            className="absolute right-3 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <CaretRight size={22} weight="bold" />
          </button>
        )}
      </div>

      {/* Caption bar */}
      <div
        className="px-6 sm:px-12 py-5 border-t border-white/10 flex items-start gap-6 text-white"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-caption"
      >
        <div className="flex-1 min-w-0">
          <div className="text-xs tracking-wider uppercase text-white/60 mb-1">
            {(PHASE_TKEY[p.phase] && t(PHASE_TKEY[p.phase])) || p.phase} · {p.percentage}% · {dateStr}
          </div>
          {p.description && (
            <div className="font-serif-dv text-lg text-white/90 leading-snug">{p.description}</div>
          )}
        </div>
        <div className="text-xs text-white/60 whitespace-nowrap shrink-0">
          {idx + 1} / {total}
        </div>
      </div>
    </div>
  );
}
